import { useState } from "react";
import { Plus, Trash2, Users } from "lucide-react";
import type { AccessGroupDto } from "@/services/api";
import { APP_ACCESS_LABELS, emptyAppAccess, type AppAccessMap } from "@/lib/appAccess";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { AccessCheckGrid } from "./AccessCheckGrid";
import { countUsersInGroup, enabledModuleKeys, type UserRow } from "./accessUtils";

interface GroupsTabProps {
  groups: AccessGroupDto[];
  users: UserRow[];
  savingGroup: boolean;
  onCreateGroup: (name: string, access: AppAccessMap) => Promise<void>;
  onUpdateGroup: (id: string, name: string, access: AppAccessMap) => Promise<void>;
  onDeleteGroup: (id: string, memberCount: number) => Promise<void>;
  onViewMembers: (groupId: string) => void;
}

export function GroupsTab({
  groups,
  users,
  savingGroup,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onViewMembers,
}: GroupsTabProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [access, setAccess] = useState<AppAccessMap>(() => emptyAppAccess());

  const openCreate = () => {
    setEditingId(null);
    setName("");
    setAccess(emptyAppAccess());
    setModalOpen(true);
  };

  const openEdit = (g: AccessGroupDto) => {
    setEditingId(g.id);
    setName(g.name);
    setAccess({ ...emptyAppAccess(), ...g.access });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (savingGroup) return;
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (editingId) {
      await onUpdateGroup(editingId, name.trim(), access);
    } else {
      await onCreateGroup(name.trim(), access);
    }
    closeModal();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          Perfis reutilizáveis que definem quais módulos aparecem no menu do BI.
        </p>
        <Button type="button" size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
          Novo grupo
        </Button>
      </div>

      <div className="space-y-3">
        {groups.map((g) => {
          const memberCount = countUsersInGroup(g.id, users);
          const modules = enabledModuleKeys(g.access);

          return (
            <article
              key={g.id}
              className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm hover:border-slate-300 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-slate-800">{g.name}</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                      <Users className="w-3 h-3" />
                      {memberCount} {memberCount === 1 ? "usuário" : "usuários"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {modules.length > 0 ? (
                      modules.map((key) => (
                        <span
                          key={key}
                          className="text-xs font-medium text-brand-800 bg-brand-50 border border-brand-100 px-2 py-0.5 rounded-md"
                        >
                          {APP_ACCESS_LABELS[key]}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">Nenhum módulo habilitado</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {memberCount > 0 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => onViewMembers(g.id)}>
                      Ver membros
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(g)}>
                    Editar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    onClick={() => void onDeleteGroup(g.id, memberCount)}
                    leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                  >
                    Excluir
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
        {groups.length === 0 && (
          <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-xl">
            Nenhum grupo cadastrado. Crie um perfil para atribuir aos usuários.
          </p>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? "Editar grupo" : "Novo grupo"}
        size="lg"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeModal} disabled={savingGroup}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSave()} loading={savingGroup} disabled={!name.trim()}>
              {editingId ? "Salvar" : "Criar grupo"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome do grupo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Equipe financeira"
          />
          <AccessCheckGrid value={access} onChange={setAccess} idPrefix={editingId ? `edit-${editingId}` : "new"} />
        </div>
      </Modal>
    </div>
  );
}
